import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from './components/Hero';
import ServicesOverview from './components/ServicesOverview';
import ProductShowcase from './components/ProductShowcase';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="relative">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : ''
        }`}
      >
        <div className="px-4 sm:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img
                src="https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/b857441a8282eff6e67d45b8b6cd5de0.jpeg"
                alt="Horizon Pathways Logo"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              />
              <span className="text-base sm:text-xl font-semibold text-gray-900">
                Horizon Pathways
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap"
              >
                Home
              </Link>
              <Link
                to="/products"
                className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap"
              >
                Products
              </Link>
              <Link
                to="/services"
                className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap"
              >
                Services
              </Link>
              <Link
                to="/about"
                className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap"
              >
                About
              </Link>
              <a
                href="#contact"
                className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap"
              >
                Contact
              </a>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3 sm:gap-4">
              <Link to="/contact" className="hidden sm:block px-4 sm:px-6 py-2 border-2 border-emerald-600 text-emerald-600 rounded-full text-sm font-medium transition-all hover:bg-emerald-600 hover:text-white whitespace-nowrap">
                Get Quote
              </Link>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* Mobile Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
            <span className="text-lg font-semibold text-gray-900">Menu</span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-8 h-8 flex items-center justify-center text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Mobile Menu Links */}
          <div className="flex-1 overflow-y-auto py-6">
            <div className="flex flex-col space-y-1 px-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors cursor-pointer"
              >
                Home
              </Link>
              <Link
                to="/products"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors cursor-pointer"
              >
                Products
              </Link>
              <Link
                to="/services"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors cursor-pointer"
              >
                Services
              </Link>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors cursor-pointer"
              >
                About
              </Link>
              <a
                href="#contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors cursor-pointer"
              >
                Contact
              </a>
            </div>

            {/* Mobile Menu CTA */}
            <div className="px-4 mt-6">
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full px-6 py-3 bg-emerald-600 text-white text-center rounded-full text-base font-medium hover:bg-emerald-700 transition-all whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <Hero />

      {/* Services Overview Section */}
      <ServicesOverview />

      {/* Product Showcase Section */}
      <ProductShowcase />

      {/* Contact Form Section */}
      <ContactForm />

      {/* Footer Section */}
      <Footer />
    </div>
  );
}
